import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Actions, Selector, State, StateContext, Store } from '@ngxs/store';
import produce from 'immer';
import _ from 'lodash';
import { Observable } from 'rxjs';
import { AbstractBrickSettingRemoteDto } from 'src/generated/signalR/abstract-brick-setting-remote-dto';
import { BrickSettingsService, ToolbarBrickSettingRemoteDto, ToolbarSettingRemoteDto, UserActionRemoteDto } from '../../../../../../../generated/swagger';
import { PresentationType } from '../../../../../../samwebDescription/samweb/settings/presentationType';
import { UserActionTyp } from '../../../../../../samwebDescription/samweb/userActions/userActions';
import { IBrickSettingStateModel, SpecificBrickSettingsStateBase } from '../specific-brick-settings-base';
import { SetToolbarSettings } from '../toolbar-settings/toolbar-settings.actions';
import { ToolbarSettingsState } from '../toolbar-settings/toolbar-settings.state';
import { ChangeShowVertical, ChangeToolbarPresentation } from './toolbar-brick-settings.actions';

export interface ToolbarBrickSettingsStateModel extends IBrickSettingStateModel<ToolbarBrickSettingRemoteDto> {}

function getDefaultSpecificBrickSetting(): ToolbarBrickSettingRemoteDto {
   return {
      commonBrickSettingRemoteDto: null,
      userActions: [],
      toolbarPresentationType: PresentationType.ICON_AND_TEXT.value,
      showVertical: false,
   };
}

@State<ToolbarBrickSettingsStateModel>({
   name: 'toolbarBrickSettings',
   defaults: {
      specificBrickSettings: {
         brickSettings: {},
         brickSettingsEditMode: null,
      },
   },
})
@Injectable()
export class ToolbarBrickSettingsState extends SpecificBrickSettingsStateBase<ToolbarBrickSettingRemoteDto>(getDefaultSpecificBrickSetting) {
   constructor(private brickSettingsService: BrickSettingsService, store: Store, actions$: Actions) {
      super(store);
   }

   @Selector([ToolbarBrickSettingsState, ToolbarBrickSettingsState.getSettingModelFn])
   public static getToolbarActionIds(state, getSettingModelFn: (state, brickInstanceId) => ToolbarBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId).userActions.map((x) => x.actionId);
      };
   }

   @Selector([ToolbarBrickSettingsState, ToolbarBrickSettingsState.getSettingModelFn])
   public static getIsVerticalViewEnabled(state, getSettingModelFn: (state, brickInstanceId) => ToolbarBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId).showVertical;
      };
   }

   @Selector([ToolbarBrickSettingsState, ToolbarBrickSettingsState.getSettingModelFn])
   public static getToolbarPresentationTypeFn(state, getSettingModelFn: (state, brickInstanceId) => ToolbarBrickSettingRemoteDto) {
      return (brickId: string) => {
         const presentationType = getSettingModelFn(state, brickId).toolbarPresentationType;
         if (_.isNil(presentationType)) {
            return PresentationType.ICON_AND_TEXT;
         }
         return PresentationType.valueOf(presentationType);
      };
   }

   @Selector([ToolbarBrickSettingsState, ToolbarBrickSettingsState.getSettingModelFn])
   public static getToolbarActionFn(state: ToolbarBrickSettingsStateModel, getSettingModelFn: (state, brickInstanceId) => ToolbarBrickSettingRemoteDto) {
      return (brickId: string, actionId: string) => {
         const settingModel = getSettingModelFn(state, brickId);
         return _(settingModel.userActions)
            .filter((x) => x.actionId === actionId)
            .head();
      };
   }

   @Selector([ToolbarBrickSettingsState.getToolbarActionFn])
   public static getActionTypeForUserActionFn(getToolbarActionFn: (brickId, actionId) => UserActionRemoteDto) {
      return (brickId: string, actionId: string) => {
         const userAction = getToolbarActionFn(brickId, actionId);
         if (_.isNil(userAction)) {
            return UserActionTyp.UNKNOWN_ACTION;
         }
         const resultValue = _.isEmpty(userAction.userActionType) ? UserActionTyp.UNKNOWN_ACTION : UserActionTyp.find(userAction.userActionType);
         return _.isNil(resultValue) ? UserActionTyp.UNKNOWN_ACTION : resultValue;
      };
   }

   @Selector([ToolbarBrickSettingsState.getToolbarActionFn, ToolbarBrickSettingsState.getActionTypeForUserActionFn])
   public static getActionIconFn(
      getToolbarActionFn: (brickId, actionId) => UserActionRemoteDto,
      getActionTypeForUserActionFn: (brickId, actionId) => UserActionTyp
   ) {
      return (brickId: string, actionId: string) => {
         const userAction = getToolbarActionFn(brickId, actionId);
         return _.isNil(userAction) || _.isEmpty(userAction.iconType)
            ? getActionTypeForUserActionFn(brickId, actionId).defaultIcon.iconName
            : userAction.iconType;
      };
   }

   @Action(ChangeToolbarPresentation)
   @ImmutableContext()
   public changeToolbarPresentation(ctx: StateContext<ToolbarBrickSettingsStateModel>, action: ChangeToolbarPresentation) {
      ctx.setState((state) => {
         const brickSettingModel = ToolbarBrickSettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.toolbarPresentationType = action.presentationType.value;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeShowVertical)
   public changeShowVertical(ctx: StateContext<ToolbarBrickSettingsStateModel>, action: ChangeShowVertical) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel = ToolbarBrickSettingsState.getSettingModelOrDefault(draft, action.brickInstanceId);
         brickSettingModel.showVertical = action.showVertical;
         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   protected onPrepareBrickSettingsToSave(
      brickInstanceId: string,
      brickSetting: ToolbarBrickSettingRemoteDto & AbstractBrickSettingRemoteDto
   ): ToolbarBrickSettingRemoteDto & AbstractBrickSettingRemoteDto {
      const tbs = this.store.selectSnapshot(ToolbarSettingsState.getToolbarEditModeSettings)(brickInstanceId, 'toolbar');
      brickSetting.userActions = tbs.userActions;
      brickSetting.toolbarPresentationType = tbs.toolbarPresentationType;
      return brickSetting;
   }

   protected doSaveSpecificSettings(brickInstanceId: string, brickSetting: ToolbarBrickSettingRemoteDto): Observable<any> {
      return this.brickSettingsService.changeToolbarBrickSettings({
         brickInstanceId: brickInstanceId,
         settings: brickSetting,
      });
   }

   protected onSettingsUpdatedByServer(brickInstanceId: string, settings: ToolbarBrickSettingRemoteDto): ToolbarBrickSettingRemoteDto {
      const tbs: ToolbarSettingRemoteDto = {
         userActions: settings.userActions,
         toolbarPresentationType: settings.toolbarPresentationType,
      };

      this.store.dispatch(new SetToolbarSettings(brickInstanceId, 'toolbar', tbs));
      return settings;
   }
}
